import { useState, useEffect, useRef } from 'react';
import { MessageCircle, UserPlus, UserMinus, UserCheck, Clock, Ban, ChevronDown, X, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ProfileActions = ({ isOwner, profile, currentUser, friendsCount = 0, onDevoteesClick }) => {
  const navigate = useNavigate();
  const [friendshipStatus, setFriendshipStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!isOwner && profile?.id) {
      fetchFriendshipStatus();
    }
  }, [isOwner, profile?.id]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  const fetchFriendshipStatus = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.get(`${API}/friends/status/${profile.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFriendshipStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch friendship status:', error);
    }
  };

  const handleMessage = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const response = await fetch(`${API}/conversations/${profile.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const conv = await response.json();
      navigate(`/messages/${conv.id}`);
    } catch (error) {
      console.error('Failed to start conversation:', error);
      navigate('/messages');
    }
  };

  const handleSendRequest = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.post(`${API}/friends/request/${profile.id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Devotee request sent');
      fetchFriendshipStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send request');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRequest = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.delete(`${API}/friends/request/${profile.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Request cancelled');
      fetchFriendshipStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel request');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptRequest = async () => {
    if (!friendshipStatus?.request_id) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.post(`${API}/friends/accept/${friendshipStatus.request_id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Devotee request accepted');
      fetchFriendshipStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept request');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectRequest = async () => {
    if (!friendshipStatus?.request_id) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.post(`${API}/friends/reject/${friendshipStatus.request_id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Request deleted');
      fetchFriendshipStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete request');
    } finally {
      setLoading(false);
    }
  };

  const handleUnfriend = async () => {
    setLoading(true);
    setShowDropdown(false);
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.delete(`${API}/friends/${profile.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Devotee removed');
      fetchFriendshipStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unfriend');
    } finally {
      setLoading(false);
    }
  };

  const handleBlock = async () => {
    setLoading(true);
    setShowDropdown(false);
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.post(`${API}/users/${profile.id}/block`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User blocked');
      fetchFriendshipStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to block user');
    } finally {
      setLoading(false);
    }
  };

  const handleUnblock = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.delete(`${API}/users/${profile.id}/block`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User unblocked');
      fetchFriendshipStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unblock user');
    } finally {
      setLoading(false);
    }
  };

  // Render friend action button based on status
  const renderFriendButton = () => {
    if (!friendshipStatus) {
      return (
        <button
          disabled
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg opacity-50"
        >
          <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm font-medium text-gray-700">Loading...</span>
        </button>
      );
    }

    const status = friendshipStatus.status;

    // Blocked state
    if (status === 'blocked') {
      if (friendshipStatus.is_blocked_by_me) {
        return (
          <button
            onClick={handleUnblock}
            disabled={loading}
            data-testid="unblock-btn"
            className="flex items-center gap-2 px-4 py-2 bg-red-100 hover:bg-red-200 rounded-lg transition-colors disabled:opacity-50"
          >
            <Ban className="w-4 h-4 text-red-600" />
            <span className="text-sm font-medium text-red-600">Unblock</span>
          </button>
        );
      }
      return (
        <button
          disabled
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg opacity-50"
        >
          <Ban className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-500">Blocked</span>
        </button>
      );
    }

    // Friends - show dropdown
    if (status === 'friends') {
      return (
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            data-testid="friends-dropdown-btn"
            className="flex items-center gap-2 px-4 py-2 bg-green-100 hover:bg-green-200 rounded-lg transition-colors"
          >
            <UserCheck className="w-4 h-4 text-green-600" />
            <span className="text-sm font-medium text-green-600">Devotees</span>
            <ChevronDown className="w-4 h-4 text-green-600" />
          </button>
          
          {showDropdown && (
            <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[150px] z-50">
              <button
                onClick={handleUnfriend}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                data-testid="unfriend-btn"
              >
                <UserMinus className="w-4 h-4" />
                Unfriend Devotee
              </button>
              <button
                onClick={handleBlock}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                data-testid="block-btn"
              >
                <Ban className="w-4 h-4" />
                Block
              </button>
            </div>
          )}
        </div>
      );
    }

    // Request sent - show cancel
    if (status === 'requested') {
      return (
        <button
          onClick={handleCancelRequest}
          disabled={loading}
          data-testid="cancel-request-btn"
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
        >
          <Clock className="w-4 h-4 text-gray-600" />
          <span className="text-sm font-medium text-gray-700">Cancel Request</span>
        </button>
      );
    }

    // Request received - show accept/delete
    if (status === 'pending') {
      return (
        <div className="flex items-center gap-2">
          <button
            onClick={handleAcceptRequest}
            disabled={loading}
            data-testid="accept-request-btn"
            className="flex items-center gap-2 px-4 py-2 bg-[#FF9933] hover:bg-[#E68A00] rounded-lg transition-colors disabled:opacity-50"
          >
            <UserCheck className="w-4 h-4 text-white" />
            <span className="text-sm font-medium text-white">Accept</span>
          </button>
          <button
            onClick={handleRejectRequest}
            disabled={loading}
            data-testid="delete-request-btn"
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-4 h-4 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Delete</span>
          </button>
        </div>
      );
    }

    // No connection - show add friend
    return (
      <button
        onClick={handleSendRequest}
        disabled={loading}
        data-testid="add-friend-btn"
        className="flex items-center gap-2 px-4 py-2 bg-[#FF9933] hover:bg-[#E68A00] rounded-lg transition-colors disabled:opacity-50"
      >
        <UserPlus className="w-4 h-4 text-white" />
        <span className="text-sm font-medium text-white">Add Devotee</span>
      </button>
    );
  };

  // Owner view
  if (isOwner) {
    return (
      <div className="flex flex-wrap gap-2 sm:gap-3 mt-4 pb-4 border-b border-gray-200">
        <button 
          onClick={() => navigate('/profile/edit')}
          data-testid="edit-profile-btn"
          className="flex items-center gap-2 px-4 py-2 bg-[#FF9933] hover:bg-[#E68A00] rounded-lg transition-colors"
        >
          <span className="text-sm font-medium text-white">Edit Profile</span>
        </button>
        <button 
          onClick={() => navigate('/settings')}
          data-testid="settings-btn"
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        >
          <span className="text-sm font-medium text-gray-700">Settings</span>
        </button>
        {/* Devotees button - visible on desktop, hidden on mobile (mobile shows FriendsList below) */}
        <button 
          onClick={onDevoteesClick}
          data-testid="devotees-btn"
          className="hidden lg:flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        >
          <Users className="w-4 h-4 text-gray-600" />
          <span className="text-sm font-medium text-gray-700">Devotees ({friendsCount})</span>
        </button>
      </div>
    );
  }

  // Other user view
  return (
    <div className="flex flex-wrap gap-2 sm:gap-3 mt-4 pb-4 border-b border-gray-200">
      {renderFriendButton()}
      
      {/* Message button - hidden if blocked */}
      {friendshipStatus?.status !== 'blocked' && (
        <button 
          onClick={handleMessage}
          data-testid="message-btn"
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        >
          <MessageCircle className="w-4 h-4 text-gray-700" />
          <span className="text-sm font-medium text-gray-700">Message</span>
        </button>
      )}
      
      {/* Devotees button - visible on desktop only */}
      <button 
        onClick={onDevoteesClick}
        data-testid="devotees-btn"
        className="hidden lg:flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
      >
        <Users className="w-4 h-4 text-gray-600" />
        <span className="text-sm font-medium text-gray-700">Devotees ({friendsCount})</span>
      </button>
    </div>
  );
};

export default ProfileActions;
